import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { endpoints } from "../config/api";
import { getAuthHeaders } from "../utils/getAuthHeaders";
import { getErrorMessage } from "../utils/getErrorMessage";
import { getServiceTokenHeaders } from "../utils/getServiceTokenHeaders";
import { resolveMaybeEncrypted } from "../utils/decryptSecretPayload";
import {
  clearInfisicalSession,
  createSecret,
  deleteSecret,
  listSecretScopes,
  listSecretNames,
  toUniversalAuthCredentials,
  updateSecretValue,
} from "../utils/infisicalClient";
import "./CredentialManager.css";

/**
 * Infisical secret manager.
 *
 * GET /secrets/infisical returns an encrypted machine identity and its configured
 * project/environment/path. Decrypted credentials are moved immediately into `authRef`; they
 * never enter React state or browser storage. Universal Auth and every v4 secret operation then
 * go straight from the browser to `INFISICAL_BASE`. The explicit Sync action is the only other
 * backend call and uses the signed-in user's token.
 *
 * Existing values are never requested. The list call is forced to `viewSecretValue=false`, and
 * `infisicalClient` allowlists metadata before anything reaches component state.
 */

const firstString = (source, keys, fallback = "") => {
  for (const key of keys) {
    const value = source?.[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return fallback;
};

/** Accept the backend's historical array wrappers as well as a single encrypted config object. */
const credentialList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.credentials)) return payload.credentials;
  if (Array.isArray(payload?.data)) return payload.data;
  if (payload?.credentials && typeof payload.credentials === "object") return [payload.credentials];
  if (payload && typeof payload === "object") return [payload];
  return [];
};

/**
 * Separate render-safe configuration from decrypted credentials. Client ID and Client Secret
 * exist only in the returned Map, which the caller places in a ref.
 */
export const splitInfisicalConfigs = (payload) => {
  const auth = new Map();
  const configs = [];

  credentialList(payload).forEach((item, index) => {
    const credentials = toUniversalAuthCredentials(item);
    const id = String(item?.id ?? `infisical_${index}`);
    const projectId = firstString(item, ["project_id", "projectId", "workspace_id", "workspaceId"]);
    const environment = firstString(item, ["environment", "environment_slug", "environmentSlug", "env"]);
    const secretPath = firstString(item, ["secret_path", "secretPath"], "/");

    if (credentials) auth.set(id, credentials);
    configs.push({
      id,
      label: firstString(item, ["name", "label"], `Infisical ${environment || index + 1}`),
      projectId,
      environment,
      secretPath: secretPath.startsWith("/") ? secretPath : `/${secretPath}`,
      ready: Boolean(credentials && projectId && environment),
    });
  });

  return { auth, configs };
};

const formatWhen = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const CredentialManager = ({ onClose }) => {
  const authRef = useRef(new Map());
  const [configs, setConfigs] = useState([]);
  const [configId, setConfigId] = useState("");
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [configError, setConfigError] = useState("");
  const [secrets, setSecrets] = useState(null);
  const [loadingSecrets, setLoadingSecrets] = useState(false);
  const [secretsError, setSecretsError] = useState("");
  const [syncError, setSyncError] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [success, setSuccess] = useState("");
  const [target, setTarget] = useState(null);
  const [secretName, setSecretName] = useState("");
  const [secretValue, setSecretValue] = useState("");
  const [secretError, setSecretError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);

  const configured = useMemo(() => configs.filter((config) => config.ready), [configs]);
  const selected = useMemo(
    () => configured.find((config) => config.id === configId) ?? null,
    [configured, configId]
  );

  const backendHeaders = useMemo(() => ({
    ...getAuthHeaders(),
    ...getServiceTokenHeaders(),
    Accept: "application/json",
  }), []);

  const fetchConfig = useCallback(async () => {
    setLoadingConfig(true);
    setConfigError("");
    setSecrets(null);
    try {
      const response = await axios.get(endpoints.adminCredentials, { headers: backendHeaders });
      const outer = await resolveMaybeEncrypted(response.data);
      const decrypted = await resolveMaybeEncrypted(outer?.data ?? outer);
      const { auth, configs: responseConfigs } = splitInfisicalConfigs(decrypted);
      let nextConfigs = responseConfigs;
      let nextAuth = auth;

      // The live backend currently returns only clientId/clientSecret. Discover the project and
      // environments directly from Infisical so no scope has to be entered or proxied.
      if (!responseConfigs.some((config) => config.ready) && auth.size > 0) {
        const discoveredConfigs = [];
        const discoveredAuth = new Map();
        for (const [sourceId, credentials] of auth) {
          const scopes = await listSecretScopes(credentials);
          scopes.forEach((scope) => {
            const id = `${sourceId}:${scope.projectId}:${scope.environment}:${scope.secretPath}`;
            discoveredConfigs.push({ ...scope, id, ready: true });
            discoveredAuth.set(id, credentials);
          });
        }
        if (discoveredConfigs.length > 0) {
          nextConfigs = discoveredConfigs;
          nextAuth = discoveredAuth;
        }
      }

      authRef.current = nextAuth;
      setConfigs(nextConfigs);
      const firstReady = nextConfigs.find((config) => config.ready);
      setConfigId((current) =>
        nextConfigs.some((config) => config.ready && config.id === current)
          ? current
          : firstReady?.id ?? ""
      );
    } catch (error) {
      authRef.current = new Map();
      setConfigs([]);
      setConfigId("");
      setConfigError(getErrorMessage(error, "Failed to load the Infisical configuration."));
    } finally {
      setLoadingConfig(false);
    }
  }, [backendHeaders]);

  const loadSecrets = useCallback(async () => {
    if (!selected) return;
    const credentials = authRef.current.get(selected.id);
    if (!credentials) {
      setSecretsError("The Infisical credentials are unavailable. Reload the configuration.");
      return;
    }

    setLoadingSecrets(true);
    setSecretsError("");
    try {
      setSecrets(await listSecretNames({ credentials, ...selected }));
    } catch (error) {
      setSecrets(null);
      setSecretsError(getErrorMessage(error, "Failed to list secrets from Infisical."));
    } finally {
      setLoadingSecrets(false);
    }
  }, [selected]);

  const syncSecrets = async () => {
    const userHeaders = getAuthHeaders();
    if (!userHeaders.Authorization) {
      setSyncError("Sign in again and retry.");
      return;
    }
    if (typeof endpoints.secretsSync !== "string" || !/^https?:\/\//.test(endpoints.secretsSync)) {
      setSyncError("The secrets sync endpoint is not configured correctly.");
      return;
    }

    setSyncing(true);
    setSyncError("");
    setSuccess("");
    try {
      await axios.request({
        method: "post",
        url: endpoints.secretsSync,
        data: {},
        headers: { ...userHeaders, Accept: "application/json" },
      });
      setSuccess("Secrets synced successfully.");
    } catch (error) {
      setSyncError(getErrorMessage(error, "Failed to sync secrets."));
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => { fetchConfig(); }, [fetchConfig]);
  useEffect(() => { if (selected) loadSecrets(); }, [selected, loadSecrets]);
  useEffect(() => () => {
    authRef.current.clear();
    clearInfisicalSession();
  }, []);

  useEffect(() => {
    if (!target) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !saving) {
        setTarget(null);
        setSecretName("");
        setSecretValue("");
        setSecretError("");
      }
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [target, saving]);

  useEffect(() => {
    if (!deleteTarget) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !deleting) {
        setDeleteTarget(null);
        setDeleteConfirm("");
        setDeleteError("");
      }
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [deleteTarget, deleting]);

  const openCreate = () => {
    setTarget({ isNew: true, key: "" });
    setSecretName("");
    setSecretValue("");
    setSecretError("");
    setSuccess("");
  };

  const openUpdate = (secret) => {
    setTarget({ isNew: false, key: secret.key });
    setSecretName("");
    setSecretValue("");
    setSecretError("");
    setSuccess("");
  };

  const closeEditor = () => {
    setTarget(null);
    setSecretName("");
    setSecretValue("");
    setSecretError("");
  };

  const saveSecret = async (event) => {
    event.preventDefault();
    if (!target || !selected) return;
    const name = target.isNew ? secretName.trim() : target.key;
    if (!name) { setSecretError("Enter a secret name."); return; }
    if (!secretValue) { setSecretError("Enter the new value."); return; }

    const credentials = authRef.current.get(selected.id);
    if (!credentials) { setSecretError("The Infisical credentials are unavailable."); return; }

    setSaving(true);
    setSecretError("");
    try {
      const operation = target.isNew ? createSecret : updateSecretValue;
      await operation({ credentials, secretName: name, secretValue, ...selected });
      const action = target.isNew ? "Created" : "Updated";
      closeEditor();
      await loadSecrets();
      setSuccess(`${action} ${name}. Its value remains write-only.`);
    } catch (error) {
      setSecretError(getErrorMessage(error, "Failed to write the secret to Infisical."));
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (secret) => {
    setDeleteTarget({ key: secret.key });
    setDeleteConfirm("");
    setDeleteError("");
    setSuccess("");
  };

  const closeDelete = () => {
    setDeleteTarget(null);
    setDeleteConfirm("");
    setDeleteError("");
  };

  const confirmDelete = async (event) => {
    event.preventDefault();
    if (!deleteTarget || !selected) return;
    if (deleteConfirm.trim().toLowerCase() !== "confirm") {
      setDeleteError('Type "confirm" to delete this secret.');
      return;
    }
    const name = deleteTarget.key;
    const credentials = authRef.current.get(selected.id);
    if (!credentials) { setDeleteError("The Infisical credentials are unavailable."); return; }

    setDeleting(true);
    setDeleteError("");
    try {
      await deleteSecret({ credentials, secretName: name, ...selected });
      closeDelete();
      await loadSecrets();
      setSuccess(`Deleted ${name}.`);
    } catch (error) {
      setDeleteError(getErrorMessage(error, "Failed to delete the secret from Infisical."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="secret-manager">
      <header className="secret-manager__header">
        <div>
          <h2>Secrets</h2>
          <p>Manage credentials  -- Secrets get synced automatically whenever updated. Use manual secret sync only when need to sync secrets with a dashboard which is not yet listed on the product</p>
        </div>
        {onClose && (
          <button type="button" onClick={onClose} className="secret-button secret-button--quiet">
            Close
          </button>
        )}
      </header>

      {configError && <p role="alert" className="secret-notice secret-notice--error">{configError}</p>}
      {secretsError && <p role="alert" className="secret-notice secret-notice--error">{secretsError}</p>}
      {syncError && <p role="alert" className="secret-notice secret-notice--error">{syncError}</p>}
      {success && <p role="status" className="secret-notice secret-notice--success">{success}</p>}

      <div className="secret-manager__toolbar">
        {configured.length > 1 && (
          <div className="secret-manager__environment">
            <label htmlFor="infisical-config">Environment</label>
            <select
              id="infisical-config" value={configId}
              onChange={(event) => { setConfigId(event.target.value); setSecrets(null); setSuccess(""); }}
            >
              {configured.map((config) => (
                <option key={config.id} value={config.id}>{config.label}</option>
              ))}
            </select>
          </div>
        )}

        <div className="secret-manager__actions">
          {!loadingConfig && selected && (
            <span className="secret-manager__count">
              {secrets ? `${secrets.length} ${secrets.length === 1 ? "secret" : "secrets"}` : "Secrets"}
            </span>
          )}
          <button
            type="button" className="secret-button secret-button--secondary"
            onClick={syncSecrets} disabled={syncing}
          >
            {syncing ? "Syncing…" : "Sync secrets manually"}
          </button>
          <button
            type="button" className="secret-button secret-button--secondary" onClick={loadSecrets}
            disabled={!selected || loadingSecrets || loadingConfig}
          >
            {loadingSecrets ? "Refreshing…" : "Refresh"}
          </button>
          <button
            type="button" className="secret-button secret-button--primary"
            onClick={openCreate} disabled={!selected || loadingSecrets}
          >
            <span aria-hidden="true">+</span> Add secret
          </button>
        </div>
      </div>

      <div className="secret-table-wrap">
        <table className="secret-table">
          <thead>
            <tr>
              <th>Secret</th>
              <th>Last updated</th>
              <th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {(loadingConfig || loadingSecrets) ? (
              [...Array(4)].map((_, row) => (
                <tr key={row}>{[...Array(3)].map((__, cell) => (
                  <td key={cell}><div className="secret-skeleton" /></td>
                ))}</tr>
              ))
            ) : secrets?.length ? secrets.map((secret) => (
              <tr key={secret.id ?? secret.key}>
                <td><span className="secret-table__key">{secret.key}</span></td>
                <td className="secret-table__date">{formatWhen(secret.updatedAt)}</td>
                <td className="secret-table__action">
                  <button type="button" className="secret-button secret-button--row" onClick={() => openUpdate(secret)}>
                    Update
                  </button>
                  <button type="button" className="secret-button secret-button--row secret-button--danger" onClick={() => openDelete(secret)}>
                    Delete
                  </button>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={3} className="secret-table__empty">
                  <div className="secret-table__empty-icon" aria-hidden="true">•••</div>
                  <strong>{loadingConfig ? "Connecting…" : selected ? "No secrets yet" : "No environment available"}</strong>
                  <span>
                    {selected
                      ? "Create your first secret to get started."
                      : "No accessible Infisical project or environment was found."}
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {target && (
        <div
          className="secret-modal-backdrop"
          onClick={() => !saving && closeEditor()}
        >
          <form
            onSubmit={saveSecret} onClick={(event) => event.stopPropagation()}
            className="secret-modal" role="dialog" aria-modal="true"
            aria-labelledby="secret-modal-title"
          >
            <div className="secret-modal__header">
              <div>
                <h3 id="secret-modal-title">{target.isNew ? "Add secret" : "Update secret"}</h3>
                <p>
                  {target.isNew
                    ? "Add a new key and its value."
                    : <>Enter a new value for <code>{target.key}</code>.</>}
                </p>
              </div>
              <button type="button" className="secret-modal__close" onClick={closeEditor} disabled={saving} aria-label="Close">×</button>
            </div>

            {target.isNew && (
              <div className="secret-field">
                <label htmlFor="secret-name">Secret name</label>
                <input
                  id="secret-name" value={secretName} autoComplete="off" spellCheck={false}
                  onChange={(event) => setSecretName(event.target.value)} autoFocus
                  placeholder="e.g. DATABASE_URL"
                />
              </div>
            )}
            <div className="secret-field">
              <label htmlFor="secret-value">New value</label>
              <textarea
                id="secret-value"
                value={secretValue} onChange={(event) => setSecretValue(event.target.value)}
                autoComplete="new-password" spellCheck={false} autoFocus={!target.isNew}
                placeholder="Enter the secret value"
              />
              <p>Existing values are never retrieved. This field is cleared after saving.</p>
            </div>

            {secretError && <p role="alert" className="secret-notice secret-notice--error">{secretError}</p>}
            <div className="secret-modal__actions">
              <button type="button" className="secret-button secret-button--secondary" onClick={closeEditor} disabled={saving}>Cancel</button>
              <button type="submit" className="secret-button secret-button--primary" disabled={saving}>
                {saving ? "Saving…" : target.isNew ? "Create secret" : "Update secret"}
              </button>
            </div>
          </form>
        </div>
      )}

      {deleteTarget && (
        <div
          className="secret-modal-backdrop"
          onClick={() => !deleting && closeDelete()}
        >
          <form
            onSubmit={confirmDelete} onClick={(event) => event.stopPropagation()}
            className="secret-modal" role="alertdialog" aria-modal="true"
            aria-labelledby="secret-delete-title"
          >
            <div className="secret-modal__header">
              <div>
                <h3 id="secret-delete-title">Delete secret</h3>
                <p>
                  Permanently delete <code>{deleteTarget.key}</code>? This cannot be undone.
                </p>
              </div>
              <button type="button" className="secret-modal__close" onClick={closeDelete} disabled={deleting} aria-label="Close">×</button>
            </div>

            <div className="secret-field">
              <label htmlFor="secret-delete-confirm">Type <code>confirm</code> to delete</label>
              <input
                id="secret-delete-confirm" value={deleteConfirm} autoComplete="off" spellCheck={false}
                onChange={(event) => setDeleteConfirm(event.target.value)} autoFocus
                placeholder="confirm"
              />
            </div>

            {deleteError && <p role="alert" className="secret-notice secret-notice--error">{deleteError}</p>}
            <div className="secret-modal__actions">
              <button type="button" className="secret-button secret-button--secondary" onClick={closeDelete} disabled={deleting}>Cancel</button>
              <button
                type="submit" className="secret-button secret-button--danger"
                disabled={deleting || deleteConfirm.trim().toLowerCase() !== "confirm"}
              >
                {deleting ? "Deleting…" : "Delete secret"}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
};

export default CredentialManager;
